import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash, FileCsv, FileText, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr, API_BASE } from "@/lib/api";
import FieldGuide from "@/components/FieldGuide";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import { fmtKg, fmtNum } from "@/lib/format";

const PACKAGES = ["Substructure", "Superstructure", "Facade", "Envelope", "Internal", "Finishes", "MEP", "Externals"];

export default function Calculator() {
  const { active } = useProjects();
  const [items, setItems] = useState([]);
  const [epds, setEpds] = useState([]);
  const [form, setForm] = useState({
    epd_id: "",
    name: "",
    category: "",
    quantity: 100,
    unit: "m3",
    factor_a1a3: 0,
    transport_km: 100,
    package: "Substructure",
  });
  const [busy, setBusy] = useState(false);
  const csvRef = useRef(null);
  const ifcRef = useRef(null);
  const [importing, setImporting] = useState("");

  useEffect(() => {
    api.get("/epds").then((r) => setEpds(r.data.items));
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/projects/${active.id}/boq`).then((r) => setItems(r.data.items));
  }, [active]);

  const totalKg = useMemo(() => items.reduce((s, i) => s + i.total_kg, 0), [items]);
  const intensity = active && active.floor_area_m2 ? totalKg / active.floor_area_m2 : 0;

  const onSelectEpd = (id) => {
    const e = epds.find((x) => x.id === id);
    if (!e) return;
    setForm({ ...form, epd_id: e.id, name: e.name, category: e.category, unit: e.unit, factor_a1a3: e.factor_a1a3 });
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${active.id}/boq`, form);
      setItems((arr) => [...arr, data]);
      toast.success("BOQ item added");
      setForm({ ...form, epd_id: "", name: "", category: "", factor_a1a3: 0 });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteItem = async (id) => {
    await api.delete(`/projects/${active.id}/boq/${id}`);
    setItems(items.filter((i) => i.id !== id));
  };

  const importFile = async (kind, file) => {
    if (!file) return;
    setImporting(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("ciq_token");
      const res = await fetch(`${API_BASE}/projects/${active.id}/boq/import-${kind}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`${kind.toUpperCase()}: ${data.imported} line${data.imported === 1 ? "" : "s"} imported`);
      const r = await api.get(`/projects/${active.id}/boq`);
      setItems(r.data.items);
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting("");
      if (csvRef.current) csvRef.current.value = "";
      if (ifcRef.current) ifcRef.current.value = "";
    }
  };

  const downloadCsvTemplate = () => {
    const csv = "name,category,quantity,unit,factor_a1a3,transport_km,package\nLow Carbon Concrete,Concrete,1000,m3,190,80,Substructure\nStructural Steel,Steel,250,t,720,300,Superstructure\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "carboniq-boq-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  return (
    <div className="animate-fade-up">
      <PageHeader guideId="calculator"
        overline="Module · EN15978 A1–A5"
        title="Embodied Carbon Calculator"
        subtitle="Compute upfront carbon from BOQ quantities, EPD factors, and transport stages."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="csv-template-btn"
              onClick={downloadCsvTemplate}
              className="border theme-border px-3 py-2 text-xs rounded-sm hover:border-gold/40 flex items-center gap-1.5"
            >
              <FileText size={14} /> CSV template
            </button>
            <input
              ref={csvRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => importFile("csv", e.target.files?.[0])}
              data-testid="csv-file-input"
            />
            <button
              data-testid="csv-import-btn"
              disabled={!!importing}
              onClick={() => csvRef.current?.click()}
              className="border theme-border px-3 py-2 text-xs rounded-sm hover:border-gold/40 flex items-center gap-1.5 disabled:opacity-50"
            >
              <FileCsv size={14} /> {importing === "csv" ? "Importing CSV…" : "Import CSV"}
            </button>
            <input
              ref={ifcRef}
              type="file"
              accept=".ifc,.ifcxml"
              hidden
              onChange={(e) => importFile("ifc", e.target.files?.[0])}
              data-testid="ifc-file-input"
            />
            <button
              data-testid="ifc-import-btn"
              disabled={!!importing}
              onClick={() => ifcRef.current?.click()}
              className="bg-gold text-black px-3 py-2 text-xs rounded-sm hover:bg-[#e0bc40] flex items-center gap-1.5 disabled:opacity-50"
            >
              <UploadSimple size={14} weight="bold" /> {importing === "ifc" ? "Parsing IFC…" : "Import IFC"}
            </button>
          </div>
        }
      />

      <section className="px-8 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock testid="calc-total" label="Total Embodied" value={fmtKg(totalKg)} accent="gold" />
        <StatBlock testid="calc-intensity" label="Intensity" value={fmtNum(intensity, 1)} unit="kgCO₂e/m²" accent="eco" />
        <StatBlock testid="calc-items" label="BOQ Items" value={fmtNum(items.length)} accent="white" />
        <StatBlock testid="calc-cost-intensity" label="kg per $" value={fmtNum(totalKg / (active.cost_usd || 1), 4)} accent="sky" />
      </section>

      <section className="px-8 pt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form
          onSubmit={addItem}
          data-testid="boq-add-form"
          className="border border theme-border theme-surface p-6 space-y-3"
        >
          <div className="overline">Add BOQ Line</div>
          <h3 className="font-serif-display text-xl">From EPD library</h3>

          <div>
            <label className="overline flex items-center gap-1 mb-1.5">Material (EPD) <FieldGuide field="boq.epd_id" /></label>
            <select
              required
              data-testid="boq-epd-select"
              value={form.epd_id}
              onChange={(e) => onSelectEpd(e.target.value)}
              className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
            >
              <option value="">Select material</option>
              {epds.map((e) => (
                <option key={e.id} value={e.id}>{e.category} · {e.name} ({e.factor_a1a3} kg/{e.unit})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Quantity <FieldGuide field="boq.quantity" /></label>
              <input
                type="number"
                step="0.01"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline block mb-1.5">Unit</label>
              <input
                value={form.unit}
                readOnly
                className="w-full theme-input/70 border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data theme-ink-muted"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Transport (km) <FieldGuide field="boq.transport_km" /></label>
              <input
                type="number"
                value={form.transport_km}
                onChange={(e) => setForm({ ...form, transport_km: Number(e.target.value) })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Package <FieldGuide field="boq.package" /></label>
              <select
                value={form.package}
                onChange={(e) => setForm({ ...form, package: e.target.value })}
                className="w-full theme-input border border theme-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              >
                {PACKAGES.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-xs theme-ink-muted font-mono-data">
            A1–A3 factor: {form.factor_a1a3} kgCO₂e/{form.unit}
          </div>

          <button
            type="submit"
            disabled={busy || !form.epd_id}
            data-testid="boq-add-btn"
            className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Plus size={16} weight="bold" /> Add line
          </button>
        </form>

        <div className="xl:col-span-2 border border theme-border theme-surface overflow-hidden">
          <div className="px-6 py-4 border-b theme-border flex items-baseline justify-between">
            <div>
              <div className="overline">BOQ · A1–A5</div>
              <div className="font-serif-display text-xl">Line items</div>
            </div>
            <div className="text-xs font-mono-data theme-ink-dim">{items.length} entries</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                <tr>
                  <th className="text-left px-6 py-3">Item</th>
                  <th className="text-left px-3 py-3">Package</th>
                  <th className="text-right px-3 py-3">Qty</th>
                  <th className="text-right px-3 py-3">Factor A1-A3</th>
                  <th className="text-right px-3 py-3">A4 kg/u</th>
                  <th className="text-right px-3 py-3">Total kgCO₂e</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} data-testid={`boq-row-${i.id}`} className="border-t border-white/5 hover:bg-black/[0.02] dark:bg-white/[0.02]">
                    <td className="px-6 py-3">
                      <div className="theme-ink">{i.name}</div>
                      <div className="text-[11px] theme-ink-dim">{i.category} · {i.unit}</div>
                    </td>
                    <td className="px-3 theme-ink-muted">{i.package}</td>
                    <td className="px-3 py-3 text-right font-mono-data">{fmtNum(i.quantity)}</td>
                    <td className="px-3 py-3 text-right font-mono-data">{fmtNum(i.factor_a1a3, 1)}</td>
                    <td className="px-3 py-3 text-right font-mono-data theme-ink-muted">{fmtNum(i.a4_factor, 2)}</td>
                    <td className="px-3 py-3 text-right font-mono-data text-gold">{fmtKg(i.total_kg)}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        data-testid={`boq-delete-${i.id}`}
                        onClick={() => deleteItem(i.id)}
                        className="theme-ink-dim hover:text-risk"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center theme-ink-dim">No BOQ items yet.</td>
                  </tr>
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr className="border-t theme-border bg-black/[0.02] dark:bg-white/[0.02]">
                    <td colSpan={5} className="px-6 py-3 overline">Total</td>
                    <td className="px-3 py-3 text-right font-mono-data text-gold text-base" data-testid="boq-total">{fmtKg(totalKg)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
